import InfoIcon from '@mui/icons-material/Info';
import LinearScaleIcon from '@mui/icons-material/LinearScale';
import { TreeItem } from '@mui/lab';
import {
  Autocomplete,
  Checkbox,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Popover,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { every, find, flatMap, isEmpty, join, keys, map, merge, orderBy, some, transform, values } from 'lodash';
import { useSnackbar } from 'notistack';
import React, { useState } from 'react';

import { getModel } from 'api/platform';
import CopyableText from 'components/atoms/CopyableText';
import ConfirmationModal from 'components/modals/ConfirmationModal';
import { modelTypeBinaryClassifier, modelTypeClassification } from 'components/Pages/Jobs/inferenceFieldsOptions';
import { ClassificationBinningParams, InferenceModelData, OrchestrationInference } from 'interfaces/calculateFeatures';
import { Model } from 'interfaces/model';
import { Slide } from 'interfaces/slide';
import { getModelId, ModelPreferencesById, ModelsByStainByType, ModelUserPreferences } from 'utils/models';
import { OrchestrationBySlideByType } from 'utils/orchestrationSelections';
import { useClassificationModelOptions } from 'utils/queryHooks/uiConstantsHooks';
import { BinaryClassifierSelectionMode, getBinaryClassifierSelectionKey } from '../../../../binaryClassifierSelections';
import { findOrchestrationByArtifactUrlPattern } from '../../../../utils';
import ClassesToUseIgnoreRadio from '../../ClassesToUseIgnoreRadio';
import { IntensityBins } from './IntensityBins';
import { ModelDetails } from './ModelDetails';
import OrchestrationRow from './OrchestrationRow';
import { validateNewOrchestration } from './validateNewOrchestration';

const findCellSegmentationModelAndOrchestrationFromArtifactUrl = ({
  stainModelInferences,
  cellSegmentationArtifactUrl: orchestrationResultArtifactUrlPattern,
}: {
  stainModelInferences: ModelsByStainByType[string];
  cellSegmentationArtifactUrl: string;
}) => {
  const allStainModelInferences = flatMap(values(stainModelInferences), (modelEntries) => values(modelEntries));
  const cellSegmentationModel = find(allStainModelInferences, (inferenceModel) => {
    const orchestrations = inferenceModel.orchestrations;
    const orchestrationByResultUrl = findOrchestrationByArtifactUrlPattern(
      orchestrations,
      orchestrationResultArtifactUrlPattern
    );
    return Boolean(orchestrationByResultUrl);
  });
  if (cellSegmentationModel) {
    // Add the cell segmentation model dependency to the orchestrations
    return {
      orchestration: findOrchestrationByArtifactUrlPattern(
        cellSegmentationModel.orchestrations,
        orchestrationResultArtifactUrlPattern
      ),
      model: cellSegmentationModel,
    };
  } else {
    console.warn('Could not find model for cell segmentation artifact url', {
      orchestrationResultArtifactUrlPattern,
      stainModelInferences,
      allStainModelInferences,
    });
    return null;
  }
};

export interface ModelRowProps {
  studyId: string;
  stain: string;
  modelType: string;
  stainModelInferences: ModelsByStainByType[string];
  modelInferenceUrl: string;
  slides: Record<string, Pick<Slide, 'stainingType' | 'originalFileName'>>;
  selectedOrchestrations: OrchestrationBySlideByType;
  setSelectedOrchestrations: (
    slideIds: string[],
    model: Partial<InferenceModelData>,
    modelType: string,
    orchestration: OrchestrationInference
  ) => void;
  setModelPreferencesByModelId?: (modelId: string, userPreferences: ModelUserPreferences) => void;
  modelPreferencesByModelId?: ModelPreferencesById;
  setSelectedOrchestrationsByClassificationType?: (modelType: string, previousModelType?: string) => void;
  setIntensityClassificationBinning?: (modelId: string, binning: ClassificationBinningParams) => void;
  binaryClassifierSelectionMode?: BinaryClassifierSelectionMode;
  disableIfNoJob?: boolean;
}

const ModelRow: React.FC<React.PropsWithChildren<ModelRowProps>> = ({
  studyId,
  stain,
  modelType,
  modelInferenceUrl,
  stainModelInferences,
  slides,
  selectedOrchestrations,
  setSelectedOrchestrations,
  modelPreferencesByModelId = {},
  setModelPreferencesByModelId,
  setSelectedOrchestrationsByClassificationType,
  setIntensityClassificationBinning,
  binaryClassifierSelectionMode = 'single',
  disableIfNoJob,
}) => {
  const modelInference = stainModelInferences[modelType]?.[modelInferenceUrl];

  const modelId = modelInference.modelId ?? getModelId(modelInference?.modelUrl);
  const placeholderDataModel: Model = {
    meta: {
      deps: {
        classesConfig: transform(
          modelInference?.classes,
          function (result: Record<string, {}>, className) {
            result[className] = {};
            return result;
          },
          {}
        ),
      },
      modelId: modelId,
      modelType: modelInference?.modelType,
    },
    url: modelInference?.modelUrl,
  };

  const { data, isLoading: isLoadingModelData } = useQuery({
    queryKey: ['model', modelId],
    queryFn: ({ signal }) => getModel(modelId, signal),
    placeholderData: placeholderDataModel,
    retry: false,
  });

  const [confirmationModal, setConfirmationModal] = useState(null);
  const [anchorElModelDetails, setAnchorElModelDetails] = useState<null | HTMLElement>(null);
  const [anchorElIntensityBins, setAnchorElIntensityBins] = useState<null | HTMLElement>(null);

  const { enqueueSnackbar } = useSnackbar();
  const { classificationModelOptions } = useClassificationModelOptions();

  const selectedModelType = modelPreferencesByModelId[modelId]?.readAs ?? modelType;
  const isBinaryClassifierModel = modelType === modelTypeBinaryClassifier.apiModelValue;
  const getSelectionModelType = (orchestration: OrchestrationInference) =>
    isBinaryClassifierModel && binaryClassifierSelectionMode === 'multiple'
      ? getBinaryClassifierSelectionKey(orchestration)
      : selectedModelType;

  const setBinaryClassifierSelectedOrchestrationsWithCellSegmentationArtifact = (
    slideIds: string[],
    newOrchestration: OrchestrationInference,
    cellSegmentationArtifactUrl: string,
    selectionModelType: string
  ) => {
    const cellSegmentationFromUrl = findCellSegmentationModelAndOrchestrationFromArtifactUrl({
      stainModelInferences,
      cellSegmentationArtifactUrl,
    });
    if (cellSegmentationFromUrl) {
      const { model, orchestration } = cellSegmentationFromUrl;
      const hasDifferentSelectedDependency = some(slideIds, (slideId) => {
        const selectedDependency = selectedOrchestrations?.[slideId]?.[model.modelType]?.orchestration;
        return Boolean(
          selectedDependency?.orchestrationId && selectedDependency.orchestrationId !== orchestration?.orchestrationId
        );
      });

      if (hasDifferentSelectedDependency && binaryClassifierSelectionMode === 'multiple') {
        enqueueSnackbar(
          'Selected binary classifier results use different cell segmentation results. Keep the selections and add a spatial join post-processing step before combining them.',
          { variant: 'warning' }
        );
        setSelectedOrchestrations(slideIds, modelInference, selectionModelType, newOrchestration);
        return;
      }

      if (hasDifferentSelectedDependency) {
        console.warn(`User selected a different ${model.modelType} - will replace with appropriate dependency`);
        setConfirmationModal({
          title: 'Confirm Dependency Replacement',
          text: (
            <Grid container direction="column">
              <Grid item mt={1}>
                <Typography variant="subtitle1">
                  The slides ({join(slideIds, ', ')}) are currently using the {model.modelType} model ({model.modelUrl}
                  ).
                </Typography>
              </Grid>
              <Grid item mt={1}>
                <Typography variant="subtitle1">Do you want to replace it with the {modelType} dependency?</Typography>
              </Grid>
              <Grid item mt={1}>
                <Typography variant="caption">Dependency URL: {cellSegmentationArtifactUrl}?</Typography>
              </Grid>
            </Grid>
          ),
          onConfirm: () => {
            console.debug('Adding cell segmentation model to orchestrations', {
              cellSegmentationArtifactUrl,
              model,
              orchestration,
            });
            setSelectedOrchestrations(slideIds, model, model.modelType, orchestration);

            setSelectedOrchestrations(slideIds, modelInference, selectionModelType, newOrchestration);
          },
        });
        return;
      } else {
        console.debug('Adding cell segmentation model to orchestrations', {
          cellSegmentationArtifactUrl,
          model,
          orchestration,
        });
        setSelectedOrchestrations(slideIds, model, model.modelType, orchestration);
      }
    }
    setSelectedOrchestrations(slideIds, modelInference, selectionModelType, newOrchestration);
  };

  const setRowModelSelectedOrchestrations = (
    slideIds: string[],
    newOrchestration: OrchestrationInference,
    selectionModelType = selectedModelType
  ) => {
    const cellSegmentationArtifactUrl = newOrchestration?.params?.deps?.detectionResults;
    if (isBinaryClassifierModel && cellSegmentationArtifactUrl) {
      // Add cell segmentation artifact url for a binary classifier model when selected, if set in the orchestration's deps
      setBinaryClassifierSelectedOrchestrationsWithCellSegmentationArtifact(
        slideIds,
        newOrchestration,
        cellSegmentationArtifactUrl,
        selectionModelType
      );
    } else {
      setSelectedOrchestrations(slideIds, modelInference, selectionModelType, newOrchestration);
    }
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setAnchorElModelDetails(event.currentTarget);
  };

  const handleCloseModelDetails = () => {
    setAnchorElModelDetails(null);
  };

  const handleCloseIntensityBins = () => {
    setAnchorElIntensityBins(null);
  };

  const onCheckOrchestration = (
    newOrchestration: OrchestrationInference,
    slideIds: string[],
    checked: boolean,
    selectionModelType = selectedModelType
  ) => {
    if (checked) {
      const message = validateNewOrchestration({
        newOrchestration,
        slideIds,
        slides,
        selectedOrchestrations,
        selectedModelType: selectionModelType,
        modelInference,
        modelType,
        stain,
        modelId,
        setSelectedOrchestrations,
        setRowModelSelectedOrchestrations: (validatedSlideIds, validatedOrchestration) =>
          setRowModelSelectedOrchestrations(validatedSlideIds, validatedOrchestration, selectionModelType),
      });
      if (isEmpty(message)) {
        setRowModelSelectedOrchestrations(slideIds, newOrchestration, selectionModelType);
      } else {
        setConfirmationModal({
          title: message.title,
          text: message.text,
          onConfirm: message.onConfirm,
        });
      }
    } else {
      setSelectedOrchestrations(slideIds, modelInference, selectionModelType, null);
    }
  };

  const isModelChecked = every(modelInference?.orchestrations, (orchestration) =>
    every(
      orchestration?.slideIds,
      (slideId) =>
        selectedOrchestrations?.[slideId]?.[getSelectionModelType(orchestration)]?.model?.modelUrl ===
        modelInference?.modelUrl
    )
  );

  const isSomeChecked = some(modelInference?.orchestrations, (orchestration) =>
    some(
      orchestration?.slideIds,
      (slideId) =>
        selectedOrchestrations?.[slideId]?.[getSelectionModelType(orchestration)]?.model?.modelUrl ===
        modelInference?.modelUrl
    )
  );

  const isModelIndeterminate = some(modelInference?.orchestrations, (orchestration) =>
    some(
      orchestration?.slideIds,
      (slideId) =>
        selectedOrchestrations?.[slideId]?.[getSelectionModelType(orchestration)]?.model?.modelUrl ===
        modelInference?.modelUrl
    )
  );

  const handleClassesToUseOrIgnoreChange = (event: React.ChangeEvent<HTMLInputElement>, classes: string[]) => {
    event.stopPropagation();
    setModelPreferencesByModelId(
      modelId,
      modelPreferencesByModelId[modelId]?.useClassesFromIgnore
        ? { ...modelPreferencesByModelId[modelId], classesToIgnore: classes }
        : { ...modelPreferencesByModelId[modelId], classesToUse: classes }
    );
  };

  const selectedClasses = modelPreferencesByModelId[modelId]?.useClassesFromIgnore
    ? modelPreferencesByModelId[modelId]?.classesToIgnore || []
    : modelPreferencesByModelId[modelId]?.classesToUse || [];

  const modelClasses = keys(data?.meta?.deps?.classesConfig) || [];
  return (
    <>
      <TreeItem
        key={modelInference?.modelUrl}
        nodeId={modelInference?.modelUrl}
        label={
          <Grid container wrap="nowrap" alignItems="center" spacing={1}>
            <Grid item>
              <Checkbox disabled checked={isModelChecked} indeterminate={!isModelChecked && isModelIndeterminate} />
            </Grid>
            <Grid item xs sx={{ minWidth: 0 }}>
              <CopyableText
                variant="body2"
                textForCopy={modelId}
                tooltipText="Copy model ID"
                copyWithButton="right"
                copyButtonProps={{ 'aria-label': 'Copy model ID', size: 'small', sx: { ml: 0.25, p: 0 } }}
                copyIconProps={{ fontSize: 'inherit' }}
                parentGridProps={{ width: 'fit-content', justifyContent: 'flex-start', sx: { maxWidth: '100%' } }}
              >
                Model ID: {modelId}
              </CopyableText>
            </Grid>
            <Grid item>
              <IconButton onClick={handleClick}>
                <InfoIcon fontSize="small" />
              </IconButton>
              <Popover
                onClick={(e) => e.stopPropagation()}
                key={modelInference?.modelUrl}
                open={Boolean(anchorElModelDetails)}
                anchorEl={anchorElModelDetails}
                onClose={handleCloseModelDetails}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                slotProps={{ paper: { style: { width: '500px' } } }}
              >
                <ModelDetails modelDetails={merge(data, placeholderDataModel)} onClosed={handleCloseModelDetails} />
              </Popover>
            </Grid>
            {modelType === modelTypeClassification.apiModelValue ? (
              <Grid item xs={5}>
                <FormControl
                  required={isModelChecked || isModelIndeterminate}
                  error={(isModelChecked || isModelIndeterminate) && isEmpty(modelPreferencesByModelId[modelId])}
                  fullWidth
                >
                  <InputLabel>Classification Model</InputLabel>
                  <Select
                    size="small"
                    value={modelPreferencesByModelId[modelId]?.readAs ?? null}
                    label="Classification Model"
                    onChange={(event) => {
                      setSelectedOrchestrationsByClassificationType(event.target.value, selectedModelType);
                      setModelPreferencesByModelId(modelId, { readAs: event.target.value });
                    }}
                    onClick={(event) => {
                      event.stopPropagation();
                      event.preventDefault();
                    }}
                  >
                    <MenuItem value={null}>
                      <em>None</em>
                    </MenuItem>
                    {map(classificationModelOptions, (option) => (
                      <MenuItem value={option.id}>{option.displayName}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            ) : (
              isSomeChecked &&
              !isEmpty(modelClasses) &&
              modelPreferencesByModelId &&
              setModelPreferencesByModelId &&
              handleClassesToUseOrIgnoreChange && (
                <>
                  <Grid item xs={3} onClick={(e) => e.stopPropagation()}>
                    <ClassesToUseIgnoreRadio
                      isToIgnore={modelPreferencesByModelId[modelId]?.useClassesFromIgnore ?? false}
                      updateIsToIgnore={(isToIgnore) =>
                        setModelPreferencesByModelId(modelId, {
                          ...modelPreferencesByModelId[modelId],
                          useClassesFromIgnore: isToIgnore,
                        })
                      }
                    />
                  </Grid>

                  <Grid item xs>
                    <Autocomplete
                      onClick={(e) => e.stopPropagation()}
                      size="small"
                      multiple
                      disableCloseOnSelect
                      limitTags={1}
                      options={modelClasses}
                      value={selectedClasses}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          onClick={(e) => e.stopPropagation()}
                          label={
                            modelPreferencesByModelId[modelId]?.useClassesFromIgnore
                              ? 'Classes To Ignore'
                              : 'Classes To Use'
                          }
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {isLoadingModelData && <CircularProgress color="inherit" size={20} />}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                          helperText={
                            !modelPreferencesByModelId[modelId]?.useClassesFromIgnore &&
                            'if not selected, all classes will be used'
                          }
                        />
                      )}
                      onChange={handleClassesToUseOrIgnoreChange}
                    />
                  </Grid>
                </>
              )
            )}
            {find(classificationModelOptions, { id: modelPreferencesByModelId[modelId]?.readAs })
              ?.needBinningParams && (
              <Grid item>
                <Tooltip title="Intensity Bins">
                  <IconButton
                    onClick={(event) => {
                      event.stopPropagation();
                      setAnchorElIntensityBins(event.currentTarget);
                    }}
                  >
                    <LinearScaleIcon />
                  </IconButton>
                </Tooltip>
                <Popover
                  onClick={(e) => e.stopPropagation()}
                  key={modelInference.modelUrl}
                  open={Boolean(anchorElIntensityBins)}
                  anchorEl={anchorElIntensityBins}
                  onClose={handleCloseIntensityBins}
                  anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                  slotProps={{ paper: { style: { width: '500px' } } }}
                >
                  <IntensityBins
                    binning={{
                      numBins: modelPreferencesByModelId[modelId]?.numBins,
                      mapValuesToBins: modelPreferencesByModelId[modelId]?.mapValuesToBins,
                    }}
                    setBinning={(binning: ClassificationBinningParams) => {
                      setIntensityClassificationBinning(modelId, binning);
                    }}
                    classes={modelInference.classes}
                    onClosed={handleCloseIntensityBins}
                  />
                </Popover>
              </Grid>
            )}
          </Grid>
        }
      >
        {map(orderBy(modelInference?.orchestrations, 'createdAt', 'desc'), (orchestration) => {
          const selectionModelType = getSelectionModelType(orchestration);
          return (
            <OrchestrationRow
              key={orchestration.orchestrationId}
              modelUrl={modelInference?.modelUrl}
              modelType={selectionModelType}
              orchestration={orchestration}
              selectedOrchestrations={selectedOrchestrations}
              onCheck={(newOrchestration: OrchestrationInference, slideIds: string[], checked: boolean) => {
                onCheckOrchestration(newOrchestration, slideIds, checked, selectionModelType);
              }}
              slides={slides}
              studyId={studyId}
              disableIfNoJob={disableIfNoJob}
            />
          );
        })}
      </TreeItem>
      {!isEmpty(confirmationModal) && (
        <ConfirmationModal
          title={confirmationModal.title}
          text={confirmationModal?.text}
          onConfirm={() => {
            confirmationModal?.onConfirm?.();
            setConfirmationModal(null);
          }}
          onCancel={() => {
            confirmationModal?.onCancel?.();
            setConfirmationModal(null);
          }}
        />
      )}
    </>
  );
};

export default ModelRow;
